import { useSyncExternalStore } from 'react';

import { bridgeErrorKind } from '@heidi/plugin-core';
import {
  listenRpaEvent,
  rpaBridge,
  type AcquireDesktopControlResult,
  type DesktopControlClientAction,
  type DesktopControlDispatchResult,
  type DesktopControlLease,
  type DesktopControlOwner,
  type DesktopControlOwnerKind,
  type DesktopControlOwnerPatch,
  type DesktopControlSnapshot,
  type RefreshDesktopControlResult,
  type ReleaseDesktopControlResult,
} from '@heidi/plugin-rpa';
import { logger, sanitizeError } from '@heidi/runtime/logger';

export const RUN_COMPUTER_USE_TASK_TOOL_NAME = 'run_computer_use_task';

export const DESKTOP_CONTROL_LEASE_REFRESH_INTERVAL_MS = 5_000;

export const DESKTOP_CONTROL_BUSY_APPROVAL_MESSAGE =
  'Another desktop task is running. Finish or cancel it before approving this one.';

export const DESKTOP_CONTROL_BUSY_TOOL_RESULT =
  'Unable to start the desktop task right now because another desktop task is already running. Ask the user to approve this step again, or try again after the current desktop task finishes.';

export const DESKTOP_CONTROL_CHANGED_EVENT = 'desktop-control-changed';

export class DesktopControlDispatchMismatchError extends Error {
  constructor(expected: string, actual: string) {
    super(
      `Unexpected desktop-control dispatch result: expected ${expected}, got ${actual}`
    );
    this.name = 'DesktopControlDispatchMismatchError';
  }
}

export class DesktopControlLeaseBusyError extends Error {
  readonly current: DesktopControlLease | null | undefined;

  constructor(current: DesktopControlLease | null | undefined) {
    super(DESKTOP_CONTROL_BUSY_TOOL_RESULT);
    this.name = 'DesktopControlLeaseBusyError';
    this.current = current;
  }
}

export interface DesktopControlReadParams {
  ownerKind?: DesktopControlOwnerKind | null;
  ownerId?: string | null;
  agentRunId?: string | null;
}

type LeaseListener = () => void;

const listeners = new Set<LeaseListener>();

let memorySnapshot: DesktopControlSnapshot = { current: null };
let eventListenerInstalled = false;

export const unwrapAcquireResult = (
  result: AcquireDesktopControlResult
):
  | { ok: true; lease: DesktopControlLease }
  | { ok: false; blocker: DesktopControlLease } =>
  result.acquired
    ? { ok: true, lease: result.lease }
    : { ok: false, blocker: result.current };

export const unwrapRefreshResult = (
  result: RefreshDesktopControlResult
):
  | { ok: true; lease: DesktopControlLease }
  | { ok: false; current: DesktopControlLease | null } =>
  result.refreshed
    ? { ok: true, lease: result.lease }
    : { ok: false, current: result.current ?? null };

function assertDispatchResult(
  response: DesktopControlDispatchResult,
  expected: 'acquire'
): Extract<DesktopControlDispatchResult, { type: 'acquire' }>;
function assertDispatchResult(
  response: DesktopControlDispatchResult,
  expected: 'refresh'
): Extract<DesktopControlDispatchResult, { type: 'refresh' }>;
function assertDispatchResult(
  response: DesktopControlDispatchResult,
  expected: 'release'
): Extract<DesktopControlDispatchResult, { type: 'release' }>;
function assertDispatchResult(
  response: DesktopControlDispatchResult,
  expected: DesktopControlDispatchResult['type']
): DesktopControlDispatchResult {
  if (response.type !== expected) {
    throw new DesktopControlDispatchMismatchError(expected, response.type);
  }
  return response;
}

const dispatchAndSyncSnapshot = async (
  action: DesktopControlClientAction
): Promise<DesktopControlDispatchResult> => {
  const response = await rpaBridge.dispatchDesktopControl(action);
  setSnapshot(response.snapshot);
  return response;
};

export const getDesktopControlBusyApprovalMessage = (
  current: DesktopControlLease | null | undefined
): string => {
  const projectTitle = current?.projectTitle?.trim();
  if (!projectTitle) return DESKTOP_CONTROL_BUSY_APPROVAL_MESSAGE;
  return `Another desktop task is running in ${projectTitle}. Finish or cancel it before approving this one.`;
};

export const isRunComputerUseTaskApproval = (toolName: string | undefined): boolean =>
  toolName === RUN_COMPUTER_USE_TASK_TOOL_NAME;

export const acquireDesktopControlLease = async (
  owner: DesktopControlOwner
): Promise<AcquireDesktopControlResult> => {
  const response = assertDispatchResult(
    await dispatchAndSyncSnapshot({ type: 'acquire', owner }),
    'acquire'
  );
  return response.result;
};

export const acquireDesktopControlLeaseOrThrow = async (
  owner: DesktopControlOwner
): Promise<DesktopControlLease> => {
  const unwrapped = unwrapAcquireResult(await acquireDesktopControlLease(owner));
  if (!unwrapped.ok) {
    throw new DesktopControlLeaseBusyError(unwrapped.blocker);
  }
  return unwrapped.lease;
};

export const refreshDesktopControlLease = async (
  leaseId: string,
  ownerPatch?: DesktopControlOwnerPatch
): Promise<RefreshDesktopControlResult> => {
  const action: DesktopControlClientAction =
    ownerPatch === undefined
      ? { type: 'refresh', leaseId }
      : { type: 'refresh', leaseId, ownerPatch };
  const response = assertDispatchResult(await dispatchAndSyncSnapshot(action), 'refresh');
  return response.result;
};

export const refreshDesktopControlLeaseOrThrow = async (
  leaseId: string,
  ownerPatch?: DesktopControlOwnerPatch
): Promise<DesktopControlLease> => {
  const unwrapped = unwrapRefreshResult(
    await refreshDesktopControlLease(leaseId, ownerPatch)
  );
  if (!unwrapped.ok) {
    throw new DesktopControlLeaseBusyError(unwrapped.current);
  }
  return unwrapped.lease;
};

export const releaseDesktopControlLease = async (
  leaseId: string
): Promise<ReleaseDesktopControlResult> => {
  const response = assertDispatchResult(
    await dispatchAndSyncSnapshot({ type: 'release', leaseId }),
    'release'
  );
  return response.result;
};

export const refreshDesktopControlSnapshot =
  async (): Promise<DesktopControlSnapshot> => {
    const snapshot = await rpaBridge.readDesktopControlSnapshot();
    setSnapshot(snapshot);
    return snapshot;
  };

export const readDesktopControlSnapshot = (): DesktopControlLease | null =>
  memorySnapshot.current ?? null;

export const getDesktopControlBlocker = (
  params: DesktopControlReadParams | null | undefined
): DesktopControlLease | null => {
  const current = readDesktopControlSnapshot();
  return getDesktopControlBlockerForLease(current, params);
};

export const getDesktopControlBlockerForLease = (
  current: DesktopControlLease | null | undefined,
  params: DesktopControlReadParams | null | undefined
): DesktopControlLease | null => {
  if (!current) return null;
  if (isSameAllowedOwner(current, params)) return null;
  return current;
};

export const subscribeDesktopControlLease = (listener: LeaseListener): (() => void) => {
  listeners.add(listener);
  installEventListener();
  void refreshDesktopControlSnapshot().catch((error: unknown) => {
    // A bounded kind and the sanitised error: never the bridge error's text.
    logger.debug('[desktop-control] snapshot refresh failed', {
      errorKind: bridgeErrorKind(error),
      error: sanitizeError(error),
    });
  });
  return () => {
    listeners.delete(listener);
  };
};

export const useDesktopControlLease = (): DesktopControlLease | null =>
  useSyncExternalStore(
    subscribeDesktopControlLease,
    readDesktopControlSnapshot,
    () => null
  );

export const useDesktopControlBlocker = (
  params: DesktopControlReadParams | null | undefined
): DesktopControlLease | null =>
  useSyncExternalStore(
    subscribeDesktopControlLease,
    () => getDesktopControlBlocker(params),
    () => null
  );

export const __resetDesktopControlLeaseForTests = (): void => {
  memorySnapshot = { current: null };
  listeners.clear();
  eventListenerInstalled = false;
};

export const __setDesktopControlLeaseSnapshotForTests = (
  current: DesktopControlLease | null
): void => {
  setSnapshot({ current });
};

const isSameLeaseSnapshot = (
  current: DesktopControlLease | null,
  next: DesktopControlLease | null
): boolean => {
  if (current === next) return true;
  if (!current || !next) return false;
  return current.leaseId === next.leaseId && current.revision === next.revision;
};

const setSnapshot = (snapshot: DesktopControlSnapshot): void => {
  const next = snapshot.current ?? null;
  if (isSameLeaseSnapshot(memorySnapshot.current ?? null, next)) return;
  memorySnapshot = { current: next };
  emitLeaseChange();
};

const emitLeaseChange = (): void => {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // isolate subscriber failures
    }
  }
};

const installEventListener = (): void => {
  if (eventListenerInstalled || !rpaBridge.isAvailable()) return;
  eventListenerInstalled = true;
  void listenRpaEvent<DesktopControlSnapshot>(DESKTOP_CONTROL_CHANGED_EVENT, (snapshot) =>
    setSnapshot(snapshot)
  ).catch((error: unknown) => {
    eventListenerInstalled = false;
    // A bounded kind and the sanitised error: never the listener error's text.
    logger.debug('[desktop-control] event listener install failed', {
      errorKind: bridgeErrorKind(error),
      error: sanitizeError(error),
    });
  });
};

const isSameAllowedOwner = (
  current: DesktopControlLease,
  params: DesktopControlReadParams | null | undefined
): boolean => {
  if (params?.ownerId && current.ownerId === params.ownerId) {
    if (!params.ownerKind) return true;
    return current.ownerKind === params.ownerKind;
  }
  return (
    current.ownerKind === 'overlay_agent' &&
    !!params?.agentRunId &&
    current.agentRunId === params.agentRunId
  );
};
